import { useTheme } from "@/components/theme/themeProvider"
import { AnonymousCell, UserCell, UserCellVariants } from "@/components/ui/layout/table/userCell"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { word } from "@/modules/core/utils/i18n"
import { formatCurrency } from "@/modules/currency/currency"
import type { TransactionEntity } from "@/modules/finances/entities/transactionEntity"
import {
  getTransactionColorStatus,
  getTransactionColorStatusDetails,
  getTransactionStatusDetailsTooltipContent,
  getTransactionStatusDetailsWording,
  getTransactionStatusWording,
} from "@/modules/finances/transactions/utils/transactionUtils"
import { PaymentMethodType } from "@/modules/finances/types/financesType"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import dayjs from "dayjs"

export function TransactionUserCell({ transaction }: { transaction: TransactionEntity }) {
  const { theme } = useTheme()
  const colorStatus = getTransactionColorStatus(transaction, theme)

  return transaction.user.isAnonymous ? (
    <AnonymousCell borderColor={colorStatus} avatarSize="size-8" />
  ) : (
    <UserCell
      member={{
        avatar: transaction.user.avatar ?? undefined,
        username: transaction.user.firstname ?? "",
        firstname: transaction.user.firstname ?? "",
        lastname: transaction.user.lastname ?? "",
      }}
      avatarSize="size-8"
      borderColor={colorStatus}
      className="[&_[data-slot=avatar-fallback]>span]:text-xs!"
    />
  )
}

export function TransactionDateCell({ transaction }: { transaction: TransactionEntity }) {
  return <Text fontStyle="BM">{dayjs(transaction.date).format("L")}</Text>
}

export function TransactionStatusCell({ transaction }: { transaction: TransactionEntity }) {
  const { theme } = useTheme()
  const wordingStatus = getTransactionStatusWording(transaction)
  const colorStatus = getTransactionColorStatus(transaction, theme)
  const wordingStatusDetails = getTransactionStatusDetailsWording(transaction)
  const colorStatusDetails = getTransactionColorStatusDetails(transaction)
  const tooltipContent = getTransactionStatusDetailsTooltipContent(transaction)

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <div className="flex flex-col">
            <Text fontStyle="LL" className={UserCellVariants({ subtitleColor: colorStatus })}>
              {wordingStatus}
            </Text>
            {wordingStatusDetails && (
              <Text
                fontStyle="BM"
                className={cn("line-clamp-2", UserCellVariants({ subtitleColor: colorStatusDetails }))}
              >
                {wordingStatusDetails}
              </Text>
            )}
          </div>
        }
      />
      {tooltipContent && (
        <TooltipContent side="bottom" className="max-w-120 wrap-break-word whitespace-break-spaces">
          <Text fontStyle="BM">{tooltipContent}</Text>
        </TooltipContent>
      )}
    </Tooltip>
  )
}

export function TransactionProductCell({ transaction }: { transaction: TransactionEntity }) {
  return (
    <Text fontStyle="BM" className="line-clamp-2 overflow-ellipsis" title={transaction.product.name}>
      {transaction.product.name}
    </Text>
  )
}

export function TransactionAmountCell({ transaction }: { transaction: TransactionEntity }) {
  const language = useLanguage()
  return <Text fontStyle="BM">{formatCurrency(transaction.amount, transaction.currency, language)}</Text>
}

export function TransactionFeeCell({ transaction }: { transaction: TransactionEntity }) {
  const language = useLanguage()
  return (
    <Text fontStyle="BM">
      {transaction.fee !== 0 ? formatCurrency(transaction.fee, transaction.currency, language) : "--"}
    </Text>
  )
}

export function TransactionPaymentMethodCell({ transaction }: { transaction: TransactionEntity }) {
  return (
    <Text fontStyle="BM">
      {transaction.outOfBandStatus
        ? word("finances.page.table.paymentMethod.atCounter")
        : transaction.paymentMethodType === PaymentMethodType.CARD
          ? word("finances.page.table.paymentMethod.card")
          : transaction.paymentMethodType === PaymentMethodType.SEPA_DEBIT
            ? word("finances.page.table.paymentMethod.sepaDebit")
            : word("finances.page.table.paymentMethod.other")}
    </Text>
  )
}
